import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { hasAnyRole, type UserRole } from '@court-sync/shared';
import { isAcademySubdomainHost } from '../lib/academyHost';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Single role or array of roles. User must have at least one. */
  requireRole?: UserRole | UserRole[];
}

export default function ProtectedRoute({ children, requireRole }: ProtectedRouteProps) {
  const { isAuthenticated, user, isLoading, isInitialized } = useAuthStore();
  const location = useLocation();

  // Wait for auth to initialize (e.g. restore session from persistence) before deciding
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated (only after auth state is known)
  if (!isAuthenticated || !user) {
    // Students/guardians/members in org or academy context should go to scoped login
    const orgMatch = location.pathname.match(/^\/club-academy\/([^/]+)/);
    const academyMatch = location.pathname.match(/^\/academy\/([^/]+)/);
    const loginPath = isAcademySubdomainHost()
      ? `/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`
      : orgMatch
        ? `/club-academy/${orgMatch[1]}/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`
        : academyMatch
          ? `/academy/${academyMatch[1]}/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`
          : `/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`;
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Check role-based access if required
  if (requireRole) {
    const roles = Array.isArray(requireRole) ? requireRole : [requireRole];
    const hasAccess = hasAnyRole(user, roles);

    if (!hasAccess) {
      const userRoles = user.roles ?? (user.rolesWithAcademy ? [...new Set(user.rolesWithAcademy.map((r) => r.role))] : []);
      return (
        <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
          <div className="text-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md mx-auto">
              <div className="text-red-500 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Access Denied
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                You don't have permission to access this page.
                {roles.length === 1
                  ? ` Required role: ${roles[0]}`
                  : ` Required one of: ${roles.join(', ')}`}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                Your roles: {userRoles.join(', ')}
              </p>
              <button
                onClick={() => window.history.back()}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}
